import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { CheckCircle, Download, Loader2, Pause, Play, Scissors, Search, Sparkles, X } from 'lucide-react'
import { Button, Card, cn } from '@trafiflow/shared-ui'
import {
  listCompanyVideos,
  getCompanyVideo,
  type CompanyVideoWithThumbnail,
} from '../../../repository/companyVideos.service'
import { VideoThumbnail } from '../../../repository/VideoThumbnail'
import type { CleanStatus, FootageData, FootageTags } from '../types'

interface FootageSelectionModalProps {
  open: boolean
  onClose: () => void
  currentFootage: FootageData
  sceneLabel?: string
  initialTab?: TabKey
  footageRepository?: FootageData[]
  onConfirm?: (selectedFootage: FootageData, clipStartSec?: number, clipEndSec?: number) => void
  /** Initial clip start time in seconds */
  initialClipStartSec?: number
  /** Initial clip end time in seconds */
  initialClipEndSec?: number
  /** Scene duration in seconds for segment generation */
  sceneDurationSec?: number
  /** Scene description for better matching */
  sceneDescription?: string
  /** Text overlay content for matching */
  sceneTextOverlay?: string
  /** Audio/voiceover content for matching */
  sceneAudio?: string
  /** AI footage tags for enhanced matching */
  aiFootageTags?: FootageTags
}

type TabKey = 'suggested' | 'all' | 'preview'

// eslint-disable-next-line react-refresh/only-export-components
export function getCategoryForSceneLabel(label?: string): string | undefined {
  if (!label) return undefined
  // Normalize: lowercase and replace underscores with spaces
  const l = label.toLowerCase().replace(/_/g, ' ')
  if (l === 'hook') return 'hook'
  if (l === 'pain' || l === 'problem') return 'ugc'
  if (l === 'solution' || l === 'benefit' || l === 'aspirational outcome') return 'product-demo'
  if (l === 'cta') return 'product-demo'
  if (l === 'product demo') return 'product-demo'
  if (l === 'talking head' || l === 'testimonial') return 'ugc'
  if (l === 'lifestyle') return 'ugc'
  if (l === 'unboxing' || l === 'ugc') return 'ugc'
  // Handle generic "Scene" labels - try to infer from context
  if (l === 'scene' || l === 'transition') return undefined // Will rely on content matching
  return undefined
}

interface VideoMatchScore {
  video: CompanyVideoWithThumbnail
  score: number
  reasons: string[]
}

// Product keyword categories for semantic matching
const PRODUCT_KEYWORDS: Record<string, string[]> = {
  leggings: ['legging', 'leggings', 'tights', 'yoga pants'],
  bra: ['bra', 'bras', 'bralette', 'sports bra', 'push-up', 'push up'],
  shapewear: ['shapewear', 'shaper', 'body shaper', 'waist trainer', 'compression'],
  underwear: ['underwear', 'panties', 'briefs', 'thong', 'boyshorts'],
  pants: ['pants', 'trousers', 'jeans', 'slacks'],
  shorts: ['shorts', 'short'],
  dress: ['dress', 'dresses', 'gown'],
  skirt: ['skirt', 'skirts'],
  top: ['top', 'blouse', 'shirt', 'tank top', 't-shirt', 'tee'],
  jacket: ['jacket', 'coat', 'blazer', 'cardigan'],
  swimwear: ['swimsuit', 'bikini', 'swimwear', 'bathing suit'],
  activewear: ['activewear', 'athletic', 'workout', 'gym wear', 'sportswear'],
}

// Products that conflict - showing one when talking about the other is confusing
const PRODUCT_CONFLICTS: Record<string, string[]> = {
  leggings: ['bra', 'dress', 'skirt', 'swimwear'],
  bra: ['leggings', 'pants', 'shorts', 'dress', 'skirt'],
  pants: ['bra', 'dress', 'skirt', 'swimwear'],
  dress: ['leggings', 'pants', 'shorts', 'bra'],
  skirt: ['leggings', 'pants', 'bra'],
  swimwear: ['leggings', 'pants', 'bra', 'dress'],
  shapewear: ['dress', 'swimwear'],
}

// Extract product keywords from text
function extractProductKeywords(text: string): string[] {
  const found: string[] = []
  const textLower = text.toLowerCase()

  for (const [category, keywords] of Object.entries(PRODUCT_KEYWORDS)) {
    for (const kw of keywords) {
      if (textLower.includes(kw)) {
        found.push(category)
        break
      }
    }
  }

  return found
}

// Check if two products conflict
function productsConflict(sceneProd: string, videoProd: string): boolean {
  const conflicts = PRODUCT_CONFLICTS[sceneProd]
  return conflicts ? conflicts.includes(videoProd) : false
}

// eslint-disable-next-line react-refresh/only-export-components
export function scoreVideoMatch(
  video: CompanyVideoWithThumbnail,
  sceneLabel?: string,
  sceneDescription?: string,
  sceneTextOverlay?: string,
  sceneAudio?: string,
  aiFootageTags?: FootageTags
): VideoMatchScore {
  const reasons: string[] = []
  let score = 0

  const category = getCategoryForSceneLabel(sceneLabel)
  const labelLower = sceneLabel?.toLowerCase() || ''
  const descLower = sceneDescription?.toLowerCase() || ''
  const textLower = sceneTextOverlay?.toLowerCase() || ''
  const audioLower = sceneAudio?.toLowerCase() || ''
  const allContent = `${labelLower} ${descLower} ${textLower} ${audioLower}`

  // SEMANTIC PRODUCT VALIDATION - Check for product mismatches
  // This prevents suggesting a "bra" clip when the scene talks about "leggings"
  const sceneProducts = extractProductKeywords(allContent)
  const videoText = `${video.aiDescription || ''} ${video.title || ''} ${(video.semanticKeywords || []).join(' ')}`.toLowerCase()
  const videoProducts = extractProductKeywords(videoText)

  if (sceneProducts.length > 0 && videoProducts.length > 0) {
    // Check for conflicts
    for (const sceneProd of sceneProducts) {
      for (const videoProd of videoProducts) {
        if (productsConflict(sceneProd, videoProd)) {
          // Severe mismatch - heavily penalize this clip
          return { video, score: -100, reasons: [`Product mismatch: scene mentions ${sceneProd} but clip shows ${videoProd}`] }
        }
      }
    }

    // Check for matches - boost score
    for (const sceneProd of sceneProducts) {
      if (videoProducts.includes(sceneProd)) {
        score += 25
        reasons.push(`Product match: ${sceneProd}`)
      }
    }
  }

  // AI Footage Tags matching (35 points max) - highest priority
  if (aiFootageTags && video.aiAnalysisStatus === 'ready') {
    // Action type match (10 points)
    if (aiFootageTags.actiontype) {
      const actionLower = aiFootageTags.actiontype.toLowerCase()
      const videoAction = (video.actionType || video.contentRole || '').toLowerCase()
      if (actionLower === videoAction || videoAction.includes(actionLower) || actionLower.includes(videoAction)) {
        score += 10
        reasons.push(`Action: ${aiFootageTags.actiontype}`)
      }
    }

    // Shot size match (8 points)
    if (aiFootageTags.shotsize) {
      const shotLower = aiFootageTags.shotsize.toLowerCase()
      const videoShot = (video.shotSize || '').toLowerCase()
      const shotMapping: Record<string, string[]> = {
        'clup': ['close', 'closeup', 'close-up', 'close_up', 'extreme_close_up'],
        'med': ['medium', 'mid', 'midshot'],
        'wide': ['wide', 'full', 'establishing'],
        'ecu': ['extreme', 'extreme_close_up', 'ecu'],
      }
      const matchTerms = shotMapping[shotLower] || [shotLower]
      if (matchTerms.some(term => videoShot.includes(term))) {
        score += 8
        reasons.push(`Shot: ${aiFootageTags.shotsize}`)
      }
    }

    // Point of view match (7 points)
    if (aiFootageTags.pointofview) {
      const povLower = aiFootageTags.pointofview.toLowerCase()
      const videoPov = (video.pointOfView || '').toLowerCase()
      if (povLower === videoPov || videoPov.includes(povLower.replace('third', '3rd').replace('first', '1st'))) {
        score += 7
        reasons.push(`POV: ${aiFootageTags.pointofview}`)
      }
    }

    // Voice match (5 points)
    if (aiFootageTags.voice) {
      const voiceLower = aiFootageTags.voice.toLowerCase()
      const videoVoice = (video.voice || '').toLowerCase()
      const voiceMapping: Record<string, string[]> = {
        'novoice': ['silent', 'no_voice', 'novoice', 'none'],
        'modeltalking': ['on_camera', 'talking', 'speaking', 'model'],
        'voiceover': ['voiceover', 'vo', 'narration'],
      }
      const matchTerms = voiceMapping[voiceLower] || [voiceLower]
      if (matchTerms.some(term => videoVoice.includes(term))) {
        score += 5
        reasons.push(`Voice: ${aiFootageTags.voice}`)
      }
    }

    // Quality match (5 points)
    if (aiFootageTags.quality) {
      const qualityLower = aiFootageTags.quality.toLowerCase()
      const videoQuality = (video.quality || '').toLowerCase()
      if (qualityLower === videoQuality || videoQuality.includes(qualityLower)) {
        score += 5
        reasons.push(`Quality: ${aiFootageTags.quality}`)
      }
    }
  }

  // Category match (25 points)
  if (category) {
    if (video.category?.toLowerCase() === category) {
      score += 25
      reasons.push('Category match')
    }
    if (video.contentRole?.toLowerCase() === category) {
      score += 20
      reasons.push('Content role match')
    }
  }

  // Tag matching (15 points max)
  if (video.tags && video.tags.length > 0) {
    let tagScore = 0
    for (const tag of video.tags) {
      const tagLower = tag.toLowerCase()
      if (allContent.includes(tagLower)) {
        tagScore += 4
        reasons.push(`Tag: ${tag}`)
      }
    }
    score += Math.min(tagScore, 15)
  }

  // Description/AI analysis matching (20 points max)
  const videoDesc = (video.aiDescription || video.description || '').toLowerCase()
  if (videoDesc && allContent) {
    // Check for keyword overlaps
    const contentWords = allContent.split(/\s+/).filter(w => w.length > 3)
    let descMatches = 0
    for (const word of contentWords) {
      if (videoDesc.includes(word)) {
        descMatches++
      }
    }
    if (descMatches > 0) {
      const descScore = Math.min(descMatches * 3, 20)
      score += descScore
      if (descMatches >= 3) {
        reasons.push('Content keywords match')
      }
    }
  }

  // Transcript matching for audio scenes (10 points)
  if (audioLower && video.transcript) {
    const transcriptLower = video.transcript.toLowerCase()
    const audioWords = audioLower.split(/\s+/).filter(w => w.length > 3)
    let transcriptMatches = 0
    for (const word of audioWords) {
      if (transcriptLower.includes(word)) {
        transcriptMatches++
      }
    }
    if (transcriptMatches >= 2) {
      score += Math.min(transcriptMatches * 2, 10)
      reasons.push('Transcript match')
    }
  }

  // Mood/tone matching (5 points)
  const moodKeywords = ['exciting', 'calm', 'urgent', 'happy', 'serious', 'fun', 'professional']
  for (const mood of moodKeywords) {
    if (allContent.includes(mood) && videoDesc.includes(mood)) {
      score += 5
      reasons.push(`Mood: ${mood}`)
      break
    }
  }

  return { video, score, reasons }
}

// eslint-disable-next-line react-refresh/only-export-components
export function getMatchConfidence(
  video: CompanyVideoWithThumbnail,
  sceneLabel?: string,
  sceneDescription?: string,
  sceneTextOverlay?: string,
  sceneAudio?: string,
  aiFootageTags?: FootageTags
): 'strong' | 'possible' | 'weak' {
  const { score } = scoreVideoMatch(video, sceneLabel, sceneDescription, sceneTextOverlay, sceneAudio, aiFootageTags)
  if (score >= 40) return 'strong'
  if (score >= 20) return 'possible'
  return 'weak'
}

function companyVideoToFootageData(video: CompanyVideoWithThumbnail): FootageData {
  return {
    id: video.id,
    filename: video.title || video.fileName,
    thumbnail: video.thumbnailUrl || '',
    duration: video.durationSec ? `${Math.floor(video.durationSec / 60)}:${String(Math.floor(video.durationSec % 60)).padStart(2, '0')}` : '0:00',
    description: video.description,
    category: video.category,
    cleanStatus: video.cleanStatus as CleanStatus | undefined,
    cleanVersionS3Key: video.cleanVersionS3Key,
    tags: video.tags?.reduce<Record<string, string>>(
      (acc, tag) => ({ ...acc, [tag]: 'YES' }),
      {}
    ),
  }
}

function VideoCard({
  video,
  isSelected,
  onSelect,
  confidenceBadge,
}: {
  video: CompanyVideoWithThumbnail
  isSelected: boolean
  onSelect: () => void
  confidenceBadge?: React.ReactNode
}) {
  const duration = video.durationSec
    ? `${Math.floor(video.durationSec / 60)}:${String(Math.floor(video.durationSec % 60)).padStart(2, '0')}`
    : '0:00'

  return (
    <button
      onClick={onSelect}
      className={cn(
        'relative flex flex-col rounded-lg overflow-hidden border-2 transition-all hover:shadow-md text-left bg-white',
        isSelected
          ? 'border-blue-600 shadow-lg ring-2 ring-blue-100'
          : 'border-gray-200 hover:border-gray-300'
      )}
    >
      <div className="relative aspect-[9/16] bg-gray-900">
        <VideoThumbnail
          videoId={video.id}
          videoStatus={video.status}
          thumbnailUrl={video.thumbnailUrl}
          className="w-full h-full"
        />

        {/* Clean status badge */}
        {video.cleanStatus === 'ready' && (
          <div className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded text-[9px] font-medium bg-green-500 text-white flex items-center gap-0.5">
            <CheckCircle className="h-2.5 w-2.5" />
            Clean
          </div>
        )}

        {confidenceBadge}

        {/* Duration */}
        <div className="absolute bottom-1.5 left-1.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-black/70 text-white">
          {duration}
        </div>

        {isSelected && (
          <div className="absolute inset-0 bg-blue-600/10 flex items-center justify-center">
            <CheckCircle className="h-8 w-8 text-blue-600" />
          </div>
        )}
      </div>

      <div className="p-2 min-h-[40px]">
        <p className="text-[11px] font-medium text-gray-800 line-clamp-2 leading-tight">
          {video.title || video.fileName}
        </p>
      </div>
    </button>
  )
}

function formatSeconds(sec: number): string {
  const mins = Math.floor(sec / 60)
  const secs = Math.floor(sec % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

interface VideoSegment {
  startSec: number
  endSec: number
  thumbnailUrl?: string
}

// Generate video segments at regular intervals for thumbnail preview
function generateVideoSegments(
  duration: number,
  segmentLength: number = 3
): VideoSegment[] {
  if (duration <= 0) return []

  const segments: VideoSegment[] = []
  let currentStart = 0

  while (currentStart < duration) {
    const endSec = Math.min(currentStart + segmentLength, duration)
    segments.push({
      startSec: currentStart,
      endSec,
    })
    currentStart = endSec
  }

  return segments
}

function SegmentThumbnail({
  segment,
  isSelected,
  onSelect,
  segmentIndex,
}: {
  segment: VideoSegment
  isSelected: boolean
  onSelect: () => void
  segmentIndex: number
}) {
  const segmentDuration = segment.endSec - segment.startSec

  return (
    <button
      onClick={onSelect}
      className={cn(
        'relative flex-shrink-0 rounded-lg overflow-hidden border-2 transition-all hover:scale-105',
        isSelected
          ? 'border-blue-500 ring-2 ring-blue-200 bg-blue-50'
          : 'border-gray-200 hover:border-blue-300 bg-gray-100'
      )}
      style={{ width: 90, height: 64 }}
    >
      {/* Segment visual representation */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className={cn(
          'text-lg font-bold',
          isSelected ? 'text-blue-600' : 'text-gray-400'
        )}>
          {segmentIndex + 1}
        </div>
      </div>

      {/* Time range label */}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent px-1.5 py-1">
        <div className="flex items-center justify-between">
          <span className="text-[9px] text-white font-medium">
            {formatSeconds(segment.startSec)}
          </span>
          <span className="text-[8px] text-white/70">
            {formatSeconds(segmentDuration)}s
          </span>
        </div>
      </div>

      {/* Selected indicator */}
      {isSelected && (
        <div className="absolute top-1 right-1">
          <CheckCircle className="h-4 w-4 text-blue-500 fill-white" />
        </div>
      )}
    </button>
  )
}

function PreviewTab({
  footage,
  videoId,
  clipStartSec,
  clipEndSec,
  onTrimChange,
  sceneDurationSec,
}: {
  footage: FootageData
  videoId?: string
  clipStartSec?: number
  clipEndSec?: number
  onTrimChange?: (start: number, end: number) => void
  sceneDurationSec?: number
}) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [trimStart, setTrimStart] = useState(clipStartSec ?? 0)
  const [trimEnd, setTrimEnd] = useState(clipEndSec ?? 0)
  const [showTrimControls, setShowTrimControls] = useState(false)
  const [segments, setSegments] = useState<VideoSegment[]>([])
  const [selectedSegmentIndex, setSelectedSegmentIndex] = useState<number | null>(null)

  const { data: videoDetail } = useQuery({
    queryKey: ['company-video-detail', videoId],
    queryFn: () => getCompanyVideo(videoId!),
    enabled: !!videoId,
    staleTime: 60000,
  })

  const videoUrl = videoDetail?.downloadUrl || videoDetail?.cleanVersionUrl

  const handleLoadedMetadata = useCallback(() => {
    if (videoRef.current) {
      const dur = videoRef.current.duration
      setDuration(dur)
      if (trimEnd === 0 || trimEnd > dur) {
        setTrimEnd(dur)
      }
      // Generate segments based on scene duration or default 3-second intervals
      const segmentLength = sceneDurationSec && sceneDurationSec > 0 ? sceneDurationSec : 3
      const newSegments = generateVideoSegments(dur, segmentLength)
      setSegments(newSegments)
    }
  }, [trimEnd, sceneDurationSec])

  const handleTimeUpdate = useCallback(() => {
    if (videoRef.current) {
      const time = videoRef.current.currentTime
      setCurrentTime(time)
      // Loop within trim range when playing
      if (showTrimControls && isPlaying && time >= trimEnd) {
        videoRef.current.currentTime = trimStart
      }
    }
  }, [showTrimControls, isPlaying, trimStart, trimEnd])

  const togglePlayPause = () => {
    if (!videoRef.current) return
    if (isPlaying) {
      videoRef.current.pause()
      setIsPlaying(false)
    } else {
      // Start from trim start if we're at the beginning
      if (showTrimControls && videoRef.current.currentTime < trimStart) {
        videoRef.current.currentTime = trimStart
      }
      videoRef.current.play()
      setIsPlaying(true)
    }
  }

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const time = parseFloat(e.target.value)
    if (videoRef.current) {
      videoRef.current.currentTime = time
      setCurrentTime(time)
    }
  }

  const handleTrimStartChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseFloat(e.target.value)
    if (val < trimEnd) {
      setTrimStart(val)
      onTrimChange?.(val, trimEnd)
    }
  }

  const handleTrimEndChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseFloat(e.target.value)
    if (val > trimStart) {
      setTrimEnd(val)
      onTrimChange?.(trimStart, val)
    }
  }

  const previewTrimRange = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = trimStart
      videoRef.current.play()
      setIsPlaying(true)
    }
  }

  const handleSelectSegment = (segment: VideoSegment, index: number) => {
    setSelectedSegmentIndex(index)
    setTrimStart(segment.startSec)
    setTrimEnd(segment.endSec)
    onTrimChange?.(segment.startSec, segment.endSec)
    setShowTrimControls(true)
    // Seek to segment start for preview
    if (videoRef.current) {
      videoRef.current.currentTime = segment.startSec
    }
  }

  const clipDuration = trimEnd - trimStart

  return (
    <div className="space-y-4">
      <div className="relative w-full aspect-video bg-black rounded-lg overflow-hidden">
        {videoUrl ? (
          <>
            <video
              ref={videoRef}
              src={videoUrl}
              className="w-full h-full object-contain"
              poster={footage.thumbnail || undefined}
              onLoadedMetadata={handleLoadedMetadata}
              onTimeUpdate={handleTimeUpdate}
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              onEnded={() => setIsPlaying(false)}
            />
            {/* Custom play/pause overlay */}
            <button
              onClick={togglePlayPause}
              className="absolute inset-0 flex items-center justify-center group"
            >
              {!isPlaying && (
                <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center transition-transform group-hover:scale-110">
                  <Play className="h-6 w-6 text-white fill-white ml-1" />
                </div>
              )}
            </button>
            {/* Playback controls bar */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3">
              <div className="flex items-center gap-2">
                <button
                  onClick={togglePlayPause}
                  className="p-1 rounded hover:bg-white/20"
                >
                  {isPlaying ? (
                    <Pause className="h-4 w-4 text-white" />
                  ) : (
                    <Play className="h-4 w-4 text-white fill-white" />
                  )}
                </button>
                <span className="text-[10px] text-white/80 min-w-[40px]">
                  {formatSeconds(currentTime)}
                </span>
                <div className="flex-1 relative">
                  <input
                    type="range"
                    min={0}
                    max={duration || 100}
                    step={0.1}
                    value={currentTime}
                    onChange={handleSeek}
                    className="w-full h-1 bg-white/30 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white"
                  />
                  {/* Trim range indicator */}
                  {showTrimControls && duration > 0 && (
                    <div
                      className="absolute top-0 h-1 bg-blue-500/50 rounded-full pointer-events-none"
                      style={{
                        left: `${(trimStart / duration) * 100}%`,
                        width: `${((trimEnd - trimStart) / duration) * 100}%`,
                      }}
                    />
                  )}
                </div>
                <span className="text-[10px] text-white/80 min-w-[40px] text-right">
                  {formatSeconds(duration)}
                </span>
              </div>
            </div>
          </>
        ) : footage.thumbnail ? (
          <>
            <img
              src={footage.thumbnail}
              alt={footage.filename}
              className="w-full h-full object-contain"
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                <Play className="h-5 w-5 text-white fill-white ml-0.5" />
              </div>
            </div>
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400">
            No preview available
          </div>
        )}
      </div>

      {/* Segment Thumbnails Strip */}
      {videoUrl && segments.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h4 className="text-xs font-semibold text-gray-700">
              Select a segment
            </h4>
            <span className="text-[10px] text-gray-500">
              {segments.length} segments available
            </span>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-gray-300">
            {segments.map((segment, index) => (
              <SegmentThumbnail
                key={index}
                segment={segment}
                isSelected={selectedSegmentIndex === index}
                onSelect={() => handleSelectSegment(segment, index)}
                segmentIndex={index}
              />
            ))}
          </div>
        </div>
      )}

      {/* Trim Controls */}
      {videoUrl && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setShowTrimControls(!showTrimControls)}
              className={cn(
                'flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium transition-colors',
                showTrimControls
                  ? 'bg-blue-100 text-blue-700'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              )}
            >
              <Scissors className="h-3 w-3" />
              {showTrimControls ? 'Trim Active' : 'Trim Clip'}
            </button>
            <Button
              variant="secondary"
              size="sm"
              icon={<Download className="h-3.5 w-3.5" />}
            >
              Download
            </Button>
          </div>

          {showTrimControls && (
            <div className="bg-gray-50 rounded-lg p-3 space-y-3">
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <label className="text-[10px] font-medium text-gray-500 uppercase tracking-wide">
                    Start
                  </label>
                  <div className="flex items-center gap-2 mt-1">
                    <input
                      type="range"
                      min={0}
                      max={duration}
                      step={0.1}
                      value={trimStart}
                      onChange={handleTrimStartChange}
                      className="flex-1 h-1.5 bg-gray-200 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-blue-500"
                    />
                    <span className="text-xs font-mono text-gray-600 min-w-[40px]">
                      {formatSeconds(trimStart)}
                    </span>
                  </div>
                </div>
                <div className="flex-1">
                  <label className="text-[10px] font-medium text-gray-500 uppercase tracking-wide">
                    End
                  </label>
                  <div className="flex items-center gap-2 mt-1">
                    <input
                      type="range"
                      min={0}
                      max={duration}
                      step={0.1}
                      value={trimEnd}
                      onChange={handleTrimEndChange}
                      className="flex-1 h-1.5 bg-gray-200 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-blue-500"
                    />
                    <span className="text-xs font-mono text-gray-600 min-w-[40px]">
                      {formatSeconds(trimEnd)}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500">
                  Clip duration: <span className="font-medium text-gray-700">{formatSeconds(clipDuration)}</span>
                </span>
                <button
                  onClick={previewTrimRange}
                  className="text-xs text-blue-600 hover:text-blue-700 hover:underline"
                >
                  Preview trim
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {footage.description && (
        <div>
          <h3 className="text-xs font-semibold mb-1.5">Description</h3>
          <p className="text-xs text-gray-500 leading-relaxed line-clamp-3">
            {footage.description}
          </p>
        </div>
      )}

      {footage.tags && Object.keys(footage.tags).length > 0 && (
        <div>
          <h3 className="text-xs font-semibold mb-2">Tags</h3>
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(footage.tags).map(([key, value]) => (
              <span
                key={key}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-gray-100 text-[10px] font-medium text-gray-600"
              >
                {key}: {value}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export function FootageSelectionModal({
  open,
  onClose,
  currentFootage,
  sceneLabel,
  initialTab,
  onConfirm,
  initialClipStartSec,
  initialClipEndSec,
  sceneDurationSec,
  sceneDescription,
  sceneTextOverlay,
  sceneAudio,
  aiFootageTags,
}: FootageSelectionModalProps) {
  const [activeTab, setActiveTab] = useState<TabKey>(initialTab ?? 'suggested')
  const [searchQuery, setSearchQuery] = useState('')
  const [searchByTranscript, setSearchByTranscript] = useState(false)
  const [selectedFootage, setSelectedFootage] = useState<FootageData | null>(null)
  const [selectedVideoId, setSelectedVideoId] = useState<string | null>(null)
  const [clipStartSec, setClipStartSec] = useState<number | undefined>(initialClipStartSec)
  const [clipEndSec, setClipEndSec] = useState<number | undefined>(initialClipEndSec)

  // Track previous open state to detect when modal opens
  const wasOpenRef = useRef(false)

  const {
    data: videosData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['company-videos', 'footage-modal'],
    queryFn: () => listCompanyVideos({ limit: 50 }),
    enabled: open,
    staleTime: 30000,
  })

  const allVideos = useMemo(() => {
    return videosData?.videos ?? []
  }, [videosData])

  // Use improved scoring to rank videos by relevance
  const suggestedVideos = useMemo(() => {
    if (!videosData?.videos || videosData.videos.length === 0) return []

    // Score all videos and sort by score
    const scoredVideos = videosData.videos
      .map(video => scoreVideoMatch(video, sceneLabel, sceneDescription, sceneTextOverlay, sceneAudio, aiFootageTags))
      .filter(scored => scored.score > 0) // Only include videos with some match
      .sort((a, b) => b.score - a.score) // Sort by score descending

    return scoredVideos.map(s => s.video)
  }, [videosData, sceneLabel, sceneDescription, sceneTextOverlay, sceneAudio])

  const filteredVideos = useMemo(() => {
    if (!searchQuery.trim()) return allVideos
    const q = searchQuery.toLowerCase()
    return allVideos.filter(
      v =>
        (v.title || v.fileName).toLowerCase().includes(q) ||
        (searchByTranscript && v.transcript?.toLowerCase().includes(q)) ||
        v.description?.toLowerCase().includes(q)
    )
  }, [allVideos, searchQuery, searchByTranscript])

  useEffect(() => {
    if (!open) return
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [open])

  // Reset modal state only when it transitions from closed to open
  // This prevents clearing selectedFootage when parent component re-renders
  useEffect(() => {
    const justOpened = open && !wasOpenRef.current
    wasOpenRef.current = open

    if (!justOpened) return

    // Reset state only when modal first opens
    setActiveTab(initialTab ?? 'suggested')
    setSearchQuery('')
    setSearchByTranscript(false)
    // If opening preview tab with existing footage, preserve the selection
    // This allows confirming trim changes on existing footage
    if (initialTab === 'preview' && currentFootage?.id) {
      setSelectedFootage(currentFootage)
      setSelectedVideoId(currentFootage.id)
    } else {
      setSelectedFootage(null)
      setSelectedVideoId(null)
    }
    setClipStartSec(initialClipStartSec)
    setClipEndSec(initialClipEndSec)
  }, [open, initialTab, initialClipStartSec, initialClipEndSec, currentFootage])

  const handleTrimChange = (start: number, end: number) => {
    setClipStartSec(start)
    setClipEndSec(end)
  }

  const handleSelectVideo = (video: CompanyVideoWithThumbnail) => {
    const footage = companyVideoToFootageData(video)
    console.log('[FootageSelectionModal] Video selected:', {
      videoId: video.id,
      videoTitle: video.title,
      footageId: footage.id,
    })
    setSelectedFootage(footage)
    setSelectedVideoId(video.id)
  }

  const handleViewPreview = () => {
    if (selectedFootage) {
      setActiveTab('preview')
    }
  }

  const handleConfirm = () => {
    // Use selected footage, or fall back to current footage if on preview tab
    const footageToConfirm = selectedFootage || (activeTab === 'preview' ? currentFootage : null)

    console.log('[FootageSelectionModal] handleConfirm called:', {
      hasSelectedFootage: !!selectedFootage,
      selectedFootageId: selectedFootage?.id,
      activeTab,
      currentFootageId: currentFootage?.id,
      footageToConfirmId: footageToConfirm?.id,
    })

    if (footageToConfirm && onConfirm) {
      console.log('[FootageSelectionModal] Confirming footage:', {
        id: footageToConfirm.id,
        filename: footageToConfirm.filename,
        clipStartSec,
        clipEndSec,
      })
      onConfirm(footageToConfirm, clipStartSec, clipEndSec)
    } else {
      console.warn('[FootageSelectionModal] No footage to confirm - selectedFootage:', selectedFootage, 'activeTab:', activeTab, 'currentFootage:', currentFootage)
    }
    onClose()
  }

  if (!open) return null

  const tabs: { key: TabKey; label: string; icon?: React.ReactNode }[] = [
    { key: 'suggested', label: 'Suggested Videos (AI)', icon: <Sparkles className="h-3 w-3" /> },
    { key: 'all', label: 'All Videos' },
    { key: 'preview', label: 'Footage Preview' },
  ]

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-40" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <Card
          className="w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
          onClick={event => event.stopPropagation()}
        >
          {/* Header with tabs */}
          <div className="border-b border-gray-200 px-5 pt-4 pb-0 flex-shrink-0">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                {tabs.map(tab => (
                  <button
                    key={tab.key}
                    className={cn(
                      'px-3 py-1.5 rounded-md text-xs font-medium flex items-center gap-1.5 transition-colors',
                      activeTab === tab.key
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    )}
                    onClick={() => setActiveTab(tab.key)}
                  >
                    {tab.icon}
                    {tab.label}
                  </button>
                ))}
              </div>
              <button
                onClick={onClose}
                className="p-1.5 rounded-md hover:bg-gray-100 text-gray-400 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className="text-xs text-gray-500 pb-3">
              Frame {currentFootage.duration || currentFootage.filename}
            </p>
          </div>

          {/* Body */}
          <div className="px-5 pb-5 pt-4 overflow-y-auto flex-1">
            {activeTab === 'preview' ? (
              <PreviewTab
                footage={selectedFootage || currentFootage}
                videoId={selectedVideoId || currentFootage.id}
                clipStartSec={clipStartSec}
                clipEndSec={clipEndSec}
                onTrimChange={handleTrimChange}
                sceneDurationSec={sceneDurationSec}
              />
            ) : activeTab === 'suggested' ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-xs text-gray-500">
                    Select a file to substitute the current video.
                  </p>
                  {!isLoading && suggestedVideos.length > 0 && (
                    <span className="text-xs text-blue-600 font-medium">
                      {suggestedVideos.length} clips matched this scene
                    </span>
                  )}
                </div>

                {isLoading && (
                  <div className="grid grid-cols-4 gap-3">
                    {Array.from({ length: 8 }).map((_, i) => (
                      <div key={i} className="rounded-lg overflow-hidden">
                        <div className="aspect-[9/16] bg-gray-100 animate-pulse" />
                        <div className="p-2">
                          <div className="h-3 bg-gray-100 animate-pulse rounded w-3/4" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {error && (
                  <div className="text-center py-8 text-red-500 text-sm">
                    Failed to load videos. Please try again.
                  </div>
                )}

                {!isLoading && !error && suggestedVideos.length === 0 && (
                  <div className="text-center py-12 bg-gray-50 rounded-lg">
                    <Sparkles className="h-8 w-8 text-gray-300 mx-auto mb-3" />
                    <p className="text-sm text-gray-500 mb-2">
                      No footage matched this scene.
                    </p>
                    <p className="text-xs text-gray-400">
                      Try the{' '}
                      <button
                        className="text-blue-600 hover:underline"
                        onClick={() => setActiveTab('all')}
                      >
                        All Videos
                      </button>{' '}
                      tab or upload new footage.
                    </p>
                  </div>
                )}

                {!isLoading && suggestedVideos.length > 0 && (
                  <div className="grid grid-cols-4 gap-3">
                    {suggestedVideos.map(video => {
                      const confidence = getMatchConfidence(video, sceneLabel, sceneDescription, sceneTextOverlay, sceneAudio, aiFootageTags)
                      return (
                        <VideoCard
                          key={video.id}
                          video={video}
                          isSelected={selectedFootage?.id === video.id}
                          onSelect={() => handleSelectVideo(video)}
                          confidenceBadge={
                            <div
                              className={cn(
                                'absolute top-1.5 right-1.5 px-1.5 py-0.5 rounded text-[9px] font-medium',
                                confidence === 'strong'
                                  ? 'bg-green-500 text-white'
                                  : confidence === 'possible'
                                    ? 'bg-yellow-500 text-white'
                                    : 'bg-gray-500 text-white'
                              )}
                            >
                              {confidence === 'strong'
                                ? 'Strong match'
                                : confidence === 'possible'
                                  ? 'Possible'
                                  : 'Weak'}
                            </div>
                          }
                        />
                      )
                    })}
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-xs text-gray-500">
                  Select a file to substitute the current video.
                </p>

                <div className="flex items-center gap-3">
                  <div className="relative flex-1">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                    <input
                      placeholder="Search by title or description"
                      value={searchQuery}
                      onChange={event => setSearchQuery(event.target.value)}
                      className="w-full pl-9 pr-3 h-8 text-xs border border-gray-200 rounded-md focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-100"
                    />
                  </div>
                  <label className="flex items-center gap-1.5 text-xs text-gray-500 whitespace-nowrap cursor-pointer">
                    <input
                      type="checkbox"
                      checked={searchByTranscript}
                      onChange={event => setSearchByTranscript(event.target.checked)}
                      className="rounded border-gray-300"
                    />
                    Search by transcript
                  </label>
                </div>

                {isLoading && (
                  <div className="flex items-center gap-2 text-xs text-gray-400">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading videos...
                  </div>
                )}

                {!isLoading && (
                  <p className="text-xs text-gray-500">{filteredVideos.length} files</p>
                )}

                {isLoading && (
                  <div className="grid grid-cols-4 gap-3">
                    {Array.from({ length: 8 }).map((_, i) => (
                      <div key={i} className="rounded-lg overflow-hidden">
                        <div className="aspect-[9/16] bg-gray-100 animate-pulse" />
                        <div className="p-2">
                          <div className="h-3 bg-gray-100 animate-pulse rounded w-3/4" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {!isLoading && (
                  <div className="grid grid-cols-4 gap-3">
                    {filteredVideos.map(video => (
                      <VideoCard
                        key={video.id}
                        video={video}
                        isSelected={selectedFootage?.id === video.id}
                        onSelect={() => handleSelectVideo(video)}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 px-5 py-3 flex items-center justify-between flex-shrink-0">
            <div>
              {activeTab !== 'preview' && selectedFootage && (
                <button
                  onClick={handleViewPreview}
                  className="text-xs text-blue-600 hover:text-blue-700 hover:underline"
                >
                  View Preview
                </button>
              )}
              {activeTab === 'preview' && (
                <button
                  onClick={() => setActiveTab('suggested')}
                  className="text-xs text-blue-600 hover:text-blue-700 hover:underline"
                >
                  ← Back to Videos
                </button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="secondary" size="sm" onClick={onClose}>
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleConfirm}
                disabled={!selectedFootage && !(activeTab === 'preview' && currentFootage?.id)}
              >
                {activeTab === 'preview' ? 'Confirm & Save' : 'Confirm Selection'}
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </>
  )
}
